import { Table as AntdTable, TablePaginationConfig } from 'antd';
import InputSearch from 'components/InputSearch';
import { IUseGetRedeemTracking } from 'features/giftCards/services/hooks/useGetRedeemTracking';
import { RangeValue } from 'rc-picker/lib/interface';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';
import { ColumnsType } from 'antd/es/table';
import { IRedeemTracking } from 'features/giftCards/services/types/redeem';
import { formatPhone } from 'utils/formatPhone';
import { formatCurrency } from 'utils/formatCurrency';
import Button from 'components/Button';
import RedeemRangerPicker from './RedeemRangerPicker';
import { useNavigate } from 'react-router-dom';
import storage from 'utils/sessionStorage';

const RedeemModalContent = ({ data, filter, loading, setfilter, search }: IUseGetRedeemTracking) => {
  const navigate = useNavigate();
  const handleAction = (id: string) => {
    navigate(`/store/${storage.shop_id.get()}/tickets/${id}`);
  };

  const columns: ColumnsType<IRedeemTracking> = [
    {
      title: 'Tickets',
      dataIndex: 'billNumber',
      key: 'billNumber',
      align: 'left',
      render: (billNumber: number) => <TicketBox>#{billNumber}</TicketBox>,
    },
    {
      title: 'Customer Name',
      dataIndex: 'customerName',
      key: 'customerName',
      align: 'left',
    },
    {
      title: 'Customer Phone',
      dataIndex: 'customerPhone',
      key: 'customerPhone',
      align: 'left',
      render: (customerPhone: string) => formatPhone(customerPhone),
    },
    {
      title: 'Amount',
      dataIndex: 'usedAmount',
      key: 'usedAmount',
      align: 'left',
      render: (usedAmount: number) => formatCurrency(usedAmount),
    },
    {
      title: 'Date Used',
      dataIndex: 'createdDate',
      key: 'createdDate',
      align: 'left',
      render: (createdDate: string) => dayjs(createdDate).format('MM-DD-YYYY hh:mmA'),
    },
    {
      title: 'Gift Card #',
      dataIndex: 'giftCode',
      key: 'giftCode',
      align: 'left',
    },
    {
      title: 'Actions',
      width: 100,
      dataIndex: 'billId',
      key: 'billId',
      align: 'center',
      render: (billId: string) => (
        <ActionButtonWrapper>
          <ActionButton type='button' onClick={() => handleAction(billId)}>
            Detail
          </ActionButton>
        </ActionButtonWrapper>
      ),
    },
  ];

  const handleChangePage = (pagination: TablePaginationConfig) => {
    const newFilter = { ...filter, page: pagination.current ?? 1 };
    search(newFilter);
  };

  const setKeySearch = (value: string) => {
    const newFilter = { ...filter, keySearch: value };
    setfilter(newFilter);
  };

  const setDateRange = (dates: RangeValue<Dayjs>) => {
    if (!dates || !dates[0] || !dates[1]) return;
    const newFilter = { ...filter, startDate: dates[0], endDate: dates[1] };
    setfilter(newFilter);
  };

  const handleFilter = () => {
    search(filter);
  };

  return (
    <StyledTableWrapper>
      <FilterWrapper>
        <InputSearch
          text={filter?.keySearch}
          onChangeText={setKeySearch}
          placeholder='Customer name/phone or card number'
        />
        <RedeemRangerPicker value={[filter?.startDate, filter?.endDate]} onChange={setDateRange} />
        <Button ntype='PRIMARY' onClick={handleFilter}>
          Search
        </Button>
      </FilterWrapper>
      <StyledTable
        columns={columns}
        dataSource={data?.content}
        rowKey={'id'}
        onChange={handleChangePage}
        pagination={{
          total: data?.totalElements ?? 0,
          pageSize: filter.size,
          current: filter.page,
          showSizeChanger: false,
        }}
        loading={loading}
      />
    </StyledTableWrapper>
  );
};

export default RedeemModalContent;

const StyledTableWrapper = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StyledTable = styled(AntdTable)`
  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th {
    background: #6fabb6;
    color: #ffffff;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    border-bottom: none;
    padding: 16px;

    &::before {
      display: none;
    }
  }

  .ant-table-tbody > tr {
    background: #ffffff;

    &:nth-child(even) {
      background: #eef4ff;
    }

    &:hover > td {
      background: #e6f0ff;
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f0f0f0;
    padding: 16px;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: #1d2129;
  }

  .ant-pagination {
    margin: 20px 0;

    .ant-pagination-item-active {
      border-color: #6fabb6;

      a {
        color: #6fabb6;
      }
    }

    .ant-pagination-item:hover {
      border-color: #6fabb6;

      a {
        color: #6fabb6;
      }
    }

    .ant-pagination-prev:hover .ant-pagination-item-link,
    .ant-pagination-next:hover .ant-pagination-item-link {
      color: #6fabb6;
      border-color: #6fabb6;
    }
  }
` as typeof AntdTable;

const ActionButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const ActionButton = styled.button`
  display: flex;
  height: 32px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  color: #fff;
  text-align: center;
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;

  background: #6fabb6;
  box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3);

  &:hover {
    background: #5a9aa4;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px 0 rgba(44, 35, 66, 0.5), 0 6px 12px -3px rgba(45, 35, 66, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const TicketBox = styled.div`
  display: flex;
  width: fit-content;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: #232f3e;
  color: #fff;
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;
